import React, { useState, useEffect, useCallback } from "react";
import { API, getToken, can } from "../api";

// ───────────────────────────────────────────────────────────────────────────
// Claim → Bordereau tab.  Where adjusters enter the Lloyd's placement metadata,
// long-tail (kv) fields, and TPA allocation that the bordereau needs — so a
// claim can move Warning → Ready.  Scoped: if the claim isn't in any bordereau
// profile's scope, shows a neutral empty state (no data-entry noise).
// All reads/writes go through /claims/{id}/bordereau/* (same generator behind
// the readiness shown here as drives the Reports → Bordereau run).
// ───────────────────────────────────────────────────────────────────────────

const NAVY = "#1F3864", BLUE = "#2E75B6", SLATE = "#64748b";
const SEM = { ready:{bg:"#E6F4EA",fg:"#1E7E34",label:"Ready"}, warning:{bg:"#FFF4CE",fg:"#8A6D00",label:"Warning"}, error:{bg:"#FBE4E4",fg:"#A12121",label:"Error"} };

const PLACEMENT_FIELDS = [
  ["coverholder_name","Coverholder Name","text"], ["umr","UMR","text"],
  ["agreement_no","Agreement No","text"], ["certificate_reference","Certificate Reference","text"],
  ["agreement_inception_date","Agreement Inception","date"], ["agreement_expiry_date","Agreement Expiry","date"],
  ["class_of_business","Class of Business","text"], ["lloyds_risk_code","Lloyd's Risk Code","text"],
  ["section_no","Section No","text"], ["original_currency","Original Currency","text"],
  ["settlement_currency","Settlement Currency","text"], ["rate_of_exchange","Rate of Exchange","number"],
  ["risk_inception_date","Risk Inception","date"], ["risk_expiry_date","Risk Expiry","date"],
  ["state_of_filing","State of Filing","text"], ["loss_county","Loss County","text"],
];
const KV_FIELDS = [
  ["CR0041","Insured Country"], ["CR0116","Location of Loss Country"],
  ["CR0106","Refer to Underwriters (Y/N)"], ["CR0107","Denial (Y/N)"],
  ["CR0122","Claimant Name"], ["CR0117","Cause of Loss Code"],
];

async function api(path, opts) {
  const r = await fetch(`${API}${path}`, { ...opts, headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json", ...(opts && opts.headers) } });
  if (!r.ok) {
    const d = (await r.json().catch(()=>({}))).detail;
    // The BDX writes return a structured detail ({message, fields|cr_refs}) on a 400.
    const msg = d && typeof d === "object" ? [d.message, (d.fields||d.cr_refs||[]).join(", ")].filter(Boolean).join(": ") : d;
    throw new Error(msg || `HTTP ${r.status}`);
  }
  return r.json();
}
const fmtDate = (v) => (v ? String(v).slice(0,10) : "");

export default function BordereauClaimTab({ claimId }) {
  const [data, setData] = useState(null);
  const [placement, setPlacement] = useState({});
  const [kv, setKv] = useState({});
  // What the server held when this tab loaded.  Save sends ONLY the fields that differ
  // from it, plus the `bdx_version` it loaded — the server refuses a Save whose version
  // is stale (or missing), so a tab left open across a bulk fill cannot wipe it.
  const [loaded, setLoaded] = useState({ placement:{}, kv:{}, version:null });
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await api(`/claims/${claimId}/bordereau/editor`);
      setData(d);
      const p = {}; PLACEMENT_FIELDS.forEach(([k,,t]) => { p[k] = t==="date" ? fmtDate(d.placement[k]) : (d.placement[k] ?? ""); });
      setPlacement(p);
      const k = {}; KV_FIELDS.forEach(([cr]) => { k[cr] = d.field_values[cr] ?? ""; });
      setKv(k);
      setLoaded({ placement:p, kv:k, version: d.bdx_version ?? null });
    } catch (e) { setError(String(e.message||e)); }
    finally { setLoading(false); }
  }, [claimId]);
  useEffect(() => { load(); }, [load]);

  const dirtyOf = (now, was) => Object.keys(now).filter(k => String(now[k] ?? "") !== String(was[k] ?? ""));
  async function savePlacement() {
    setError(null); setNotice(null);
    const dirty = dirtyOf(placement, loaded.placement);
    if (!dirty.length) { setNotice("No placement changes to save."); return; }
    setSaving("placement");
    try { const body={ expected_version: loaded.version }; dirty.forEach(k=>{ body[k]= placement[k]===""?null:placement[k]; });
      await api(`/claims/${claimId}/bordereau/placement`, { method:"PUT", body: JSON.stringify(body) }); await load();
    } catch(e){ setError(String(e.message||e)); } finally { setSaving(""); }
  }
  async function saveKv() {
    setError(null); setNotice(null);
    const dirty = dirtyOf(kv, loaded.kv);
    if (!dirty.length) { setNotice("No field changes to save."); return; }
    setSaving("kv");
    try { const values={}; dirty.forEach(cr=>{ values[cr]=kv[cr]; });   // "" clears the value
      await api(`/claims/${claimId}/bordereau/field-values`, { method:"PUT", body: JSON.stringify({ values, expected_version: loaded.version }) }); await load();
    } catch(e){ setError(String(e.message||e)); } finally { setSaving(""); }
  }
  async function setInclusion(val) {  // val: true | false | null(auto)
    setSaving("inc"); setError(null);
    try { await api(`/claims/${claimId}/bordereau/inclusion`, { method:"PUT", body: JSON.stringify({ included: val }) }); await load(); }
    catch(e){ setError(String(e.message||e)); } finally { setSaving(""); }
  }
  // (TPA allocation add/remove moved to the Parties tab / IA-TPA arc — see note below.)

  if (loading) return <div style={{padding:24,color:SLATE}}>Loading bordereau editor…</div>;
  if (error && !data) return <div style={{padding:16,background:SEM.error.bg,color:SEM.error.fg,borderRadius:6}}>{error}</div>;
  const r = data.readiness || {};
  const inc = data.inclusion || {};
  const eligible = !!inc.eligible;
  const canManage = can("manage_bordereau_profiles");
  const sev = SEM[r.status] || SEM.warning;
  const ctrl = { padding:"6px 9px", border:"1px solid #cbd5e1", borderRadius:5, fontSize:13, width:"100%" };
  const lab = { fontSize:11, color:SLATE, fontWeight:600, textTransform:"uppercase", letterSpacing:0.3 };
  const fmeta = data.field_meta || { placement:{}, kv:{} };
  // Label + Lloyd's CDR required/conditional badge (mirrors Mandatory/Conditional).
  const fieldLab = (text, meta) => (
    <span style={{ ...lab, display:"flex", alignItems:"center", gap:6 }}>
      {text}
      {meta && meta.mandatory
        ? <span title="Required by the Lloyd's bordereau (CDR Mandatory)" style={{color:"#A12121",fontWeight:800}}>● Required</span>
        : (meta && meta.note
            ? <span title={meta.note} style={{color:"#8A6D00",fontWeight:700,cursor:"help"}}>● Conditional</span>
            : null)}
    </span>
  );
  const card = { background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:18, marginBottom:16 };
  const btn = (busy)=>({ padding:"8px 16px", background:busy?"#94a3b8":NAVY, color:"white", border:"none", borderRadius:6, fontWeight:700, cursor:busy?"default":"pointer" });

  return (
    <div style={{padding:"6px 2px", maxWidth:980}}>
      {/* inclusion control — always shown so an admin can force-include/exclude */}
      <div style={card}>
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <h3 style={{margin:0,color:NAVY,fontSize:16}}>Bordereau Inclusion</h3>
          {canManage ? (
            <select style={{...ctrl,width:210}} disabled={saving==="inc"}
              value={inc.value===true?"include":inc.value===false?"exclude":"auto"}
              onChange={e=>{const v=e.target.value; setInclusion(v==="auto"?null:v==="include");}}>
              <option value="auto">Auto (by underwriter)</option>
              <option value="include">Force include</option>
              <option value="exclude">Force exclude</option>
            </select>
          ) : (
            <span style={{fontWeight:700,color:NAVY}}>{inc.value===true?"Force include":inc.value===false?"Force exclude":"Auto"}</span>
          )}
          <span style={{fontSize:13,fontWeight:700,color: eligible?SEM.ready.fg:SLATE}}>
            {eligible ? "● In the bordereau" : "● Not in the bordereau"}
          </span>
        </div>
        <div style={{fontSize:12,color:SLATE,marginTop:8}}>
          {(inc.value===null||inc.value===undefined)
            ? `Auto — ${inc.derived ? "included because a participating underwriter is on the slip" : "not included (no participating underwriter on this claim)"}.`
            : (inc.value ? "Manually force-included by an admin." : "Manually force-excluded by an admin.")}
          {!canManage && " Admins can change this."}
        </div>
      </div>
      {error && <div style={{padding:"8px 12px",background:SEM.error.bg,color:SEM.error.fg,borderRadius:6,marginBottom:12,fontSize:13}}>{error}</div>}
      {notice && !error && <div style={{padding:"8px 12px",background:"#F1F5F9",color:SLATE,borderRadius:6,marginBottom:12,fontSize:13}}>{notice}</div>}

      {!eligible ? (
        <div style={{padding:24,textAlign:"center",color:SLATE,background:"white",border:"1px solid #e2e8f0",borderRadius:10}}>
          <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>Not in the bordereau</div>
          No participating underwriter and not force-included, so there’s no bordereau data to enter.
          {canManage && " Use the inclusion control above to add it manually."}
        </div>
      ) : (
      <>
      {r.in_scope && (
      <div style={{display:"flex",alignItems:"center",gap:12,background:sev.bg,border:`1px solid ${sev.fg}33`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
        <span style={{background:sev.fg,color:"white",fontWeight:700,fontSize:12,padding:"3px 12px",borderRadius:14}}>BDX {sev.label}</span>
        <span style={{color:"#334155",fontSize:13}}>
          {r.issue_count===0 ? "All required bordereau data is present." :
            `${r.issue_count} item(s) to resolve: ${r.issues.map(i=>i.field).join(", ")}`}
        </span>
        <span style={{marginLeft:"auto",fontSize:12,color:SLATE}}>Profile: {r.profile_name}</span>
      </div>
      )}

      <div style={{fontSize:12,color:SLATE,marginBottom:12}}>
        <span style={{color:"#A12121",fontWeight:800}}>● Required</span> = Lloyd's bordereau mandatory ·{" "}
        <span style={{color:"#8A6D00",fontWeight:700}}>● Conditional</span> = required in certain cases (hover for the rule).{" "}
        You can still save and run the report with gaps — they show as warnings, not blockers.
      </div>

      {/* placement */}
      <div style={card}>
        <div style={{display:"flex",alignItems:"center",marginBottom:12}}>
          <h3 style={{margin:0,color:NAVY,fontSize:16}}>Lloyd’s Placement</h3>
          <button onClick={savePlacement} disabled={saving==="placement"} style={{...btn(saving==="placement"),marginLeft:"auto"}}>
            {saving==="placement"?"Saving…":"Save Placement"}</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          {PLACEMENT_FIELDS.map(([k,label,t])=>(
            <label key={k} style={{display:"flex",flexDirection:"column",gap:3}}>
              {fieldLab(label, fmeta.placement[k])}
              <input style={ctrl} type={t==="date"?"date":(t==="number"?"number":"text")}
                value={placement[k]??""} onChange={e=>setPlacement(p=>({...p,[k]:e.target.value}))}/>
            </label>
          ))}
        </div>
      </div>

      {/* long-tail kv */}
      <div style={card}>
        <div style={{display:"flex",alignItems:"center",marginBottom:12}}>
          <h3 style={{margin:0,color:NAVY,fontSize:16}}>Additional Lloyd’s Fields</h3>
          <button onClick={saveKv} disabled={saving==="kv"} style={{...btn(saving==="kv"),marginLeft:"auto"}}>
            {saving==="kv"?"Saving…":"Save Fields"}</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          {KV_FIELDS.map(([cr,label])=>(
            <label key={cr} style={{display:"flex",flexDirection:"column",gap:3}}>
              {fieldLab(label, fmeta.kv[cr])}
              <input style={ctrl} value={kv[cr]??""} onChange={e=>setKv(k=>({...k,[cr]:e.target.value}))}/>
            </label>
          ))}
        </div>
      </div>

      {/* TPA Allocation intentionally NOT here (2026-05-29).  It is a property
          of the TPA assignment (allocates invoice amounts among TPA-participating
          underwriters), belongs on the Parties tab for TPA claims only, and is
          irrelevant to IA work.  The BDX report CONSUMES it for TPA fee columns
          when Ops/TPA runs the report.  Editor + gating ship with the IA/TPA arc
          (matter_type, migration 049, held for Walters sign-off). */}
      </>
      )}
    </div>
  );
}
